// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * Modal reutilizable del Design System.
 * Overlay + contenedor centrado + botón cerrar. Usa variables de tema.
 * @file Modal.jsx
 */

import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useModalStackLayer } from '../../hooks/ui/useModalStackLayer';
import './Modal.css';

const MODAL_ICONS = {
  error: '🛠️',
  warning: '⚠️',
  success: '✅',
  info: 'ℹ️',
  upload: '📤',
  profile: '👤',
};

/**
 * @param {Object} props
 * @param {boolean} [props.open]
 * @param {boolean} [props.isOpen]
 * @param {() => void} props.onClose
 * @param {string} [props.title]
 * @param {string} [props.message]
 * @param {string} [props.type] - error | warning | success | info | upload | profile
 * @param {React.ReactNode} [props.children]
 * @param {React.ReactNode} [props.content]
 * @param {string} [props.modalType] - Si es 'profile', no se muestra header del Modal; solo el content.
 * @param {string} [props.className]
 * @param {boolean} [props.hideFooter]
 * @param {() => void} [props.onConfirm]
 * @param {() => void} [props.onCancel]
 * @param {string} [props.confirmText]
 * @param {string} [props.cancelText]
 * @param {boolean} [props.showCancel]
 */
function Modal({
  open: openProp,
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  children,
  content,
  modalType,
  className = '',
  hideFooter,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  showCancel,
}) {
  const { t } = useLanguage();
  const open = openProp ?? isOpen ?? false;
  const { backdropZ, modalZ } = useModalStackLayer(open);
  const rawContent = children ?? content;
  const hasExplicitContent = rawContent != null;
  const messageText = message != null && message !== '' ? message : null;
  const bodyContent =
    hasExplicitContent ? rawContent : (messageText ? <p className="modal-message">{messageText}</p> : null);
  const icon = MODAL_ICONS[type] || 'ℹ️';

  useEffect(() => {
    if (!open) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [open, onClose]);

  if (!open) return null;

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const modalRoot = typeof document !== 'undefined' ? (document.getElementById('archat-modal-root') || document.body) : null;
  if (!portalTarget) return null;

  /* Perfil: solo renderizar el content (ProfileModal con su propio header). Sin wrapper ni header duplicado. */
  if (modalType === 'profile' && rawContent) {
    return createPortal(rawContent, modalRoot);
  }

  const handleConfirm = () => {
    if (typeof onConfirm === 'function') onConfirm();
    onClose();
  };
  const handleCancel = () => {
    if (typeof onCancel === 'function') onCancel();
    onClose();
  };

  const contentMarkup = (
    <div
      className={`modal-overlay ${className}`.trim()}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'archat-modal-title' : undefined}
      style={{ zIndex: backdropZ }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ zIndex: modalZ }}>
        <div className="modal-header">
          <span className="modal-icon" aria-hidden="true">
            {icon}
          </span>
          {title && (
            <h3 id="archat-modal-title" className="modal-title">
              {title}
            </h3>
          )}
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>
        <div className="modal-body">
          {bodyContent}
        </div>
        {!hideFooter && (onConfirm != null || onCancel != null || messageText) && (
          <div className="modal-footer">
            {showCancel && (
              <button
                type="button"
                className="modal-button modal-button-secondary"
                onClick={handleCancel}
              >
                {cancelText || t('common.cancel')}
              </button>
            )}
            <button
              type="button"
              className="modal-button modal-button-primary"
              onClick={handleConfirm}
            >
              {confirmText || t('common.accept')}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  /* Portal a document.body para que el modal quede siempre en primer plano,
     fuera del stacking context de ProfileModal y del resto de la app. */
  return createPortal(contentMarkup, document.body);
}

export default Modal;
